import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";

import Home from "../pages/home";
import AuthLayout from "./layout-routes/auth-lr";
import Loading from "../components/Loading";
import AccountRouter from "./layout-routes/account-lr";
import AuthGuard from "./layout-routes/account-lr/AuthGuard";
import NotFoundScreen from "../components/NotFoundScreen";
import AppToast from "../components/AppToast";

const LoginOrRegister = lazy(() => import("../pages/auth/loginOrRegister"));

const BasicUserDetail = lazy(() =>
  import("../pages/account/basicUserDetailsForm"),
);
const BillingDetails = lazy(() =>
  import("../pages/payment/billingDetailsForm"),
);
const PaymentPage = lazy(() => import("../pages/payment"));
const PaymentLinkRedirector = lazy(() =>
  import("../pages/payment/components/PaymentLinkRedirector"),
);

const CompetitionDetailPage = lazy(() => import("../pages/competitionDetails"));
const ResultsPage = lazy(() => import("../pages/results"));

const ImageViewerPage = lazy(() => import("../pages/imageViewer"));

const PaymentForm = lazy(() => import("../pages/payment/form"));
const PaymentVerificationRequestFrom = lazy(() =>
  import("../pages/payment/verification-request-form"),
);

const QualificationResultWall = lazy(() =>
  import("../pages/qualification-result-wall"),
);

const KPLQualificationSurveyForm = lazy(() =>
  import("../pages/kpl-qualifiers-survey"),
);

const Contests = lazy(() => import("../pages/public-contests"));

const MagazineListing = lazy(() => import("../pages/magazine"));

const ResultList = lazy(() => import("../pages/result-list"));
const TalentPassPreSaleQuiz = lazy(() =>
  import("../pages/talent-pass/pre-sale-quiz"),
);
const TalentPassPreSaleQuizRecommendation = lazy(() =>
  import("../pages/talent-pass/pre-sale-quiz/recommendation"),
);
const BuildItStaticPage = lazy(() =>
  import("../pages/talent-pass/build-it-static"),
);
const ColorWizardsStaticPage = lazy(() =>
  import("../pages/talent-pass/color-wizards-static"),
);
const ColorWizardsTshirtPage = lazy(() =>
  import("../pages/talent-pass/color-wizards-tshirt"),
);
const BuildItJuniorStaticPage = lazy(() =>
  import("../pages/talent-pass/build-it-junior-static"),
);
const PaintMaestroStaticPage = lazy(() =>
  import("../pages/talent-pass/paint-maestro-static"),
);
const ReciteEnglishJuniorStaticPage = lazy(() =>
  import("../pages/talent-pass/recite-english-junior-static"),
);
const HandwritingStaticPage = lazy(() =>
  import("../pages/talent-pass/handwriting-static"),
);
const MasterOratorStaticPage = lazy(() =>
  import("../pages/talent-pass/master-orator-static"),
);
const DanceWizardsStaticPage = lazy(() =>
  import("../pages/talent-pass/dance-wizards-static"),
);
const ClassicalDanceStaticPage = lazy(() =>
  import("../pages/talent-pass/classical-dance-static"),
);
const SingingStarsStaticPage = lazy(() =>
  import("../pages/talent-pass/singing-stars-static"),
);
const InstrumentalGeniusStaticPage = lazy(() =>
  import("../pages/talent-pass/instrumental-genius-static"),
);
const TellUrTaleStaticPage = lazy(() =>
  import("../pages/talent-pass/tell-ur-tale-static"),
);
const ReciteEnglishStaticPage = lazy(() =>
  import("../pages/talent-pass/recite-english-static"),
);
const ReciteHindiStaticPage = lazy(() =>
  import("../pages/talent-pass/recite-hindi-static"),
);
const ShlokaStaticPage = lazy(() =>
  import("../pages/talent-pass/shloka-static"),
);
const AddOnFollowupPage = lazy(() =>
  import("../pages/talent-pass/addon-followup"),
);
const BuildItLevel2Page = lazy(() => import("../pages/kpl-level-2/build-it"));
const BuildItTalentPassV2Page = lazy(() =>
  import("../pages/talent-pass-v2/build-it"),
);
const ColorWizardsTalentPassV2Page = lazy(() =>
  import("../pages/talent-pass-v2/color-wizards"),
);
const ShlokaTalentPassV2Page = lazy(() =>
  import("../pages/talent-pass-v2/shloka"),
);
const YogaTalentPassV2Page = lazy(() => import("../pages/talent-pass-v2/yoga"));


const PhoneAuthenticationForm = AuthLayout(<LoginOrRegister />);
const BasicUserDetailsForm = AuthLayout(<BasicUserDetail />);
const BillingDetailsForm = AuthLayout(<BillingDetails />);

const ScrollToTop = (props) => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  return <>{props.children}</>;
};

const Router = () => {
  return (
    <BrowserRouter>
      <Suspense fallback={<Loading />}>
        <ScrollToTop>
          <div className="h-100 w-100">
            <AppToast />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route
                path="authentication"
                element={<PhoneAuthenticationForm />}
              />
              <Route
                path="account/basic-details"
                element={<AuthGuard component={<BasicUserDetailsForm />} />}
              />
              <Route path="account/*" element={<AccountRouter />} />
              <Route path="payment">
                <Route
                  path="billing-details"
                  element={<BillingDetailsForm />}
                />
                <Route
                  path="checkout/:contestName/:contestId/:amount"
                  element={<PaymentLinkRedirector />}
                />
                <Route path=":contestId/checkout" element={<PaymentPage />} />
                <Route path="form/:paymentId" element={<PaymentForm />} />
                <Route
                  path="verification-request-form"
                  element={<PaymentVerificationRequestFrom />}
                />
              </Route>
              <Route path="/contests" element={<Contests />} />
              <Route path="/magazines" element={<MagazineListing />} />
              <Route path="competition">
                <Route
                  path="details/:competitionId"
                  element={<AuthGuard component={<CompetitionDetailPage />} />}
                />
              </Route>
              <Route path="poster-viewer" element={<ImageViewerPage />} />
              <Route path="results/:partnerDomain" element={<ResultsPage />} />
              <Route
                path="qualifications/:KPLId"
                element={<QualificationResultWall />}
              />
              <Route
                path="qualifications/:partnerId/:KPLId"
                element={<QualificationResultWall />}
              />
              <Route
                path="/qualifications/survey/:KPLId"
                element={<KPLQualificationSurveyForm />}
              />
              <Route path="/result-list" element={<ResultList />} />
              <Route
                path="/talent-pass/membership"
                element={<TalentPassPreSaleQuiz />}
              />
              <Route
                path="/talent-pass/membership/recommendation"
                element={<TalentPassPreSaleQuizRecommendation />}
              />
              {/* Build It is back on v1 at its main URL (Oct 2026), with v2's
                  WhatsApp button and early phone capture added. v2 stays
                  reachable at /talent-pass/v2/build-it. */}
              <Route
                path="/talent-pass/build-it"
                element={<BuildItStaticPage />}
              />
              {/* Color Wizards URLs are swapped: the main URL
                  serves v2 and v1 stays at /talent-pass/v2/color-wizards.
                  Swap the two elements back to revert. */}
              <Route
                path="/talent-pass/color-wizards"
                element={<ColorWizardsTalentPassV2Page />}
              />
              <Route
                path="/talent-pass/color-wizards-tshirt"
                element={<ColorWizardsTshirtPage />}
              />
              <Route
                path="/talent-pass/build-it-junior"
                element={<BuildItJuniorStaticPage />}
              />
              <Route
                path="/talent-pass/paint-maestro"
                element={<PaintMaestroStaticPage />}
              />
              <Route
                path="/talent-pass/recite-english-junior"
                element={<ReciteEnglishJuniorStaticPage />}
              />
              <Route
                path="/talent-pass/handwriting-champs"
                element={<HandwritingStaticPage />}
              />
              <Route
                path="/talent-pass/master-orator"
                element={<MasterOratorStaticPage />}
              />
              <Route
                path="/talent-pass/dance-wizards"
                element={<DanceWizardsStaticPage />}
              />
              <Route
                path="/talent-pass/classical-dance"
                element={<ClassicalDanceStaticPage />}
              />
              <Route
                path="/talent-pass/singing-stars"
                element={<SingingStarsStaticPage />}
              />
              <Route
                path="/talent-pass/instrumental-genius"
                element={<InstrumentalGeniusStaticPage />}
              />
              <Route
                path="/talent-pass/tell-ur-tale"
                element={<TellUrTaleStaticPage />}
              />
              <Route
                path="/talent-pass/recite-english"
                element={<ReciteEnglishStaticPage />}
              />
              <Route
                path="/talent-pass/recite-hindi"
                element={<ReciteHindiStaticPage />}
              />
              {/* Shloka URLs are swapped, like Build It's: the main URL serves
                  v2 and v1 stays at /talent-pass/v2/shloka. Swap back to revert. */}
              <Route
                path="/talent-pass/shloka"
                element={<ShlokaTalentPassV2Page />}
              />
              <Route
                path="/talent-pass/v2/build-it"
                element={<BuildItTalentPassV2Page />}
              />
              <Route
                path="/talent-pass/v2/shloka"
                element={<ShlokaStaticPage />}
              />
              {/* Yoga Champs has no v1 page, so v2 lives on the main URL only. */}
              <Route
                path="/talent-pass/yoga"
                element={<YogaTalentPassV2Page />}
              />
              <Route
                path="/talent-pass/v2/color-wizards"
                element={<ColorWizardsStaticPage />}
              />
              <Route
                path="/talent-pass/addon-followup"
                element={<AddOnFollowupPage />}
              />
              <Route
                path="/kpl-level-2/build-it"
                element={<BuildItLevel2Page />}
              />
              <Route path="*" element={<NotFoundScreen />} />
            </Routes>
          </div>
        </ScrollToTop>
      </Suspense>
    </BrowserRouter>
  );
};

export default Router;
